// Verb Groups — collapsible cards grouping every verb by conjugation pattern
// (regular, the three stem-change families, yo-only and fully irregular), each
// with a visual diagram. Ported from the legacy views.groups with three
// transformations: window.SPANISH_DATA.* → direct ES imports, inline
// onclick="navigate(...)" → data-inf + addEventListener, and the global `app`
// → a mount-element parameter.
import { ALL_VERBS, IRREGULAR_FORMS, conjugate } from "../data/index.js";
import { navigate } from "../router/router.js";

export function groupsView(app) {
  const regulars = ALL_VERBS.filter((v) => v.group === "regular");
  const irregulars = ALL_VERBS.filter((v) => v.group === "irregular");
  const stemChange = ALL_VERBS.filter((v) => v.group === "stem-change");

  // Sub-categorize irregulars: yo-only verbs change only in the first-person
  // singular of the present tense.
  const yoOnly = irregulars.filter((v) => {
    const irr = IRREGULAR_FORMS[v.inf];
    if (!irr || !irr.present) return false;
    const reg = conjugate(v.inf, "present").regular;
    const flags = irr.present.map((f, i) => f !== reg[i]);
    return flags[0] && !flags[1] && !flags[2] && !flags[3] && !flags[4] && !flags[5];
  });
  const fullyIrregular = irregulars.filter((v) => !yoOnly.includes(v));

  // Sub-categorize stem-changes by the vowel shift seen in the yo form.
  function stemPattern(verb) {
    const irr = IRREGULAR_FORMS[verb.inf];
    if (!irr || !irr.present) return "other";
    const yo = irr.present[0];
    if (yo.includes("ie")) return "e→ie";
    if (yo.includes("ue")) return "o→ue";
    if (verb.inf.includes("e") && yo.match(/i[^e]/)) return "e→i";
    return "other";
  }
  const eToIe = stemChange.filter((v) => stemPattern(v) === "e→ie");
  const oToUe = stemChange.filter((v) => stemPattern(v) === "o→ue");
  const eToI = stemChange.filter((v) => stemPattern(v) === "e→i");
  const otherStem = stemChange.filter(
    (v) => !eToIe.includes(v) && !oToUe.includes(v) && !eToI.includes(v)
  );

  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Verb Groups</h2>
      <p class="text-sage-600 text-sm mb-6">Spanish verbs fall into patterns. Master the patterns and you've mastered hundreds of verbs at once.</p>

      <div class="space-y-4" id="groups-container"></div>
    </section>
  `;

  const container = app.querySelector("#groups-container");

  // ---- Visual diagrams for each group ----

  // Boot diagram: highlights the four "changed" cells (yo, tú, él, ellos)
  // around the unchanged nosotros/vosotros (the "heel" of the boot).
  function bootDiagram(title, regularExample, changedForms) {
    return `
      <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">${title} — present tense</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="relative">
            <svg width="0" height="0" style="position:absolute"></svg>
            <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0">${changedForms.yo}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200">${changedForms.nos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.tu}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200 border-t-0">${changedForms.vos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.el}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-r-lg border-2 border-red-300 border-l-0 border-t-0">${changedForms.ellos}</div>
            </div>
            <p class="text-xs text-sage-400 mt-2 text-center">👢 the "boot" shape</p>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-red-300 rounded-sm align-middle"></span> <b>Red cells change</b> — the stressed syllable shifts.</p>
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-sage-200 rounded-sm align-middle"></span> <b>Grey cells stay regular</b> — the stress falls on the ending, not the stem.</p>
            <p class="mt-2 italic">Example: <b>${regularExample}</b></p>
          </div>
        </div>
      </div>
    `;
  }

  function regularDiagram() {
    return `
      <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The three patterns — present tense</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-AR · hablar</p>
            <p class="text-xs text-sage-400 mb-2">to speak</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>yo habl<span class="text-sage-900 font-bold">o</span></div>
              <div>tú habl<span class="text-sage-900 font-bold">as</span></div>
              <div>él habl<span class="text-sage-900 font-bold">a</span></div>
              <div>nos habl<span class="text-sage-900 font-bold">amos</span></div>
              <div>vos habl<span class="text-sage-900 font-bold">áis</span></div>
              <div>ellos habl<span class="text-sage-900 font-bold">an</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-ER · comer</p>
            <p class="text-xs text-sage-400 mb-2">to eat</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>yo com<span class="text-sage-900 font-bold">o</span></div>
              <div>tú com<span class="text-sage-900 font-bold">es</span></div>
              <div>él com<span class="text-sage-900 font-bold">e</span></div>
              <div>nos com<span class="text-sage-900 font-bold">emos</span></div>
              <div>vos com<span class="text-sage-900 font-bold">éis</span></div>
              <div>ellos com<span class="text-sage-900 font-bold">en</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-IR · vivir</p>
            <p class="text-xs text-sage-400 mb-2">to live</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>yo viv<span class="text-sage-900 font-bold">o</span></div>
              <div>tú viv<span class="text-sage-900 font-bold">es</span></div>
              <div>él viv<span class="text-sage-900 font-bold">e</span></div>
              <div>nos viv<span class="text-sage-900 font-bold">imos</span></div>
              <div>vos viv<span class="text-sage-900 font-bold">ís</span></div>
              <div>ellos viv<span class="text-sage-900 font-bold">en</span></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 -ER and -IR are identical except for nosotros (-emos vs -imos) and vosotros (-éis vs -ís).</p>
      </div>
    `;
  }

  function yoOnlyDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">Only the "yo" form changes</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
            <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg border-2 border-red-300 font-bold">ten<span class="underline">go</span></div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tenemos</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tienes*</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tenéis</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tiene*</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tienen*</div>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p>The yo-only "-go" group: <b>tener, poner, salir, hacer, venir, decir, traer</b>. Memorize as a chant:</p>
            <p class="mt-2 font-mono text-sage-800">tengo · pongo · salgo<br>hago · vengo · digo · traigo</p>
            <p class="mt-2 text-sage-400 italic">* tener also stem-changes (e→ie); the pure yo-only verbs like poner/salir/hacer don't.</p>
          </div>
        </div>
      </div>
    `;
  }

  function fullyIrregularDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The big four — must be memorized</p>
        <div class="grid sm:grid-cols-2 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">ser <span class="text-sage-400 font-normal text-xs">— to be (essence)</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>yo <b class="text-red-700">soy</b></div>
              <div>tú <b class="text-red-700">eres</b></div>
              <div>él <b class="text-red-700">es</b></div>
              <div>nos <b class="text-red-700">somos</b></div>
              <div>vos <b class="text-red-700">sois</b></div>
              <div>ellos <b class="text-red-700">son</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">estar <span class="text-sage-400 font-normal text-xs">— to be (state)</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>yo <b class="text-red-700">estoy</b></div>
              <div>tú estás</div>
              <div>él está</div>
              <div>nos estamos</div>
              <div>vos estáis</div>
              <div>ellos están</div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">ir <span class="text-sage-400 font-normal text-xs">— to go</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>yo <b class="text-red-700">voy</b></div>
              <div>tú <b class="text-red-700">vas</b></div>
              <div>él <b class="text-red-700">va</b></div>
              <div>nos <b class="text-red-700">vamos</b></div>
              <div>vos <b class="text-red-700">vais</b></div>
              <div>ellos <b class="text-red-700">van</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">tener <span class="text-sage-400 font-normal text-xs">— to have</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>yo <b class="text-red-700">tengo</b></div>
              <div>tú <b class="text-red-700">tienes</b></div>
              <div>él <b class="text-red-700">tiene</b></div>
              <div>nos tenemos</div>
              <div>vos tenéis</div>
              <div>ellos <b class="text-red-700">tienen</b></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 Fun fact: <b>ser</b> and <b>ir</b> are identical in the preterite (fui, fuiste, fue…). Context decides.</p>
      </div>
    `;
  }

  function renderGroup(title, color, description, verbs, infographic) {
    const sec = document.createElement("div");
    sec.className = "bg-white rounded-2xl border border-sage-100 p-5";
    sec.innerHTML = `
      <div class="flex items-center justify-between mb-2 cursor-pointer group-header">
        <div>
          <h3 class="font-bold text-lg text-sage-800">${title} <span class="text-sage-400 text-sm font-normal">(${verbs.length})</span></h3>
          <p class="text-sm text-sage-600 mt-1">${description}</p>
        </div>
        <span class="text-sage-400 text-xl group-arrow transition-transform">›</span>
      </div>
      <div class="group-body hidden mt-3">
        ${infographic || ""}
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2 mt-1">Verbs in this group</p>
        <div class="flex flex-wrap gap-1.5">
          ${verbs
            .slice(0, 80)
            .map(
              (v) => `
            <button data-inf="${v.inf}"
              class="verb-chip text-xs px-2.5 py-1 rounded-full ${color} hover:opacity-80 transition">
              ${v.emoji ? v.emoji + " " : ""}${v.inf}
            </button>
          `
            )
            .join("")}
          ${
            verbs.length > 80
              ? `<span class="text-xs text-sage-400 px-2 py-1">+${verbs.length - 80} more</span>`
              : ""
          }
        </div>
      </div>
    `;
    sec.querySelector(".group-header").addEventListener("click", () => {
      const body = sec.querySelector(".group-body");
      const arrow = sec.querySelector(".group-arrow");
      body.classList.toggle("hidden");
      arrow.style.transform = body.classList.contains("hidden") ? "" : "rotate(90deg)";
    });
    sec.querySelectorAll(".verb-chip").forEach((btn) =>
      btn.addEventListener("click", () =>
        navigate("verb-detail", { inf: btn.dataset.inf })
      )
    );
    container.appendChild(sec);
  }

  renderGroup(
    "Regular verbs",
    "bg-sage-100 text-sage-700",
    "These follow the textbook -ar / -er / -ir patterns with no surprises. Once you know hablar, comer, and vivir, you can conjugate them all.",
    regulars,
    regularDiagram()
  );
  renderGroup(
    "Stem-changing: e → ie",
    "bg-sand-100 text-sand-700",
    "The stressed 'e' becomes 'ie' in all forms except nosotros and vosotros. Examples: pensar → pienso, querer → quiero.",
    eToIe,
    bootDiagram(
      "pensar (to think)",
      "querer → quiero, entender → entiendo, empezar → empiezo",
      { yo: "pienso", tu: "piensas", el: "piensa", nos: "pensamos", vos: "pensáis", ellos: "piensan" }
    )
  );
  renderGroup(
    "Stem-changing: o → ue",
    "bg-sand-100 text-sand-700",
    "The stressed 'o' becomes 'ue' in all forms except nosotros and vosotros. Examples: poder → puedo, dormir → duermo.",
    oToUe,
    bootDiagram(
      "poder (to be able)",
      "dormir → duermo, volver → vuelvo, contar → cuento",
      { yo: "puedo", tu: "puedes", el: "puede", nos: "podemos", vos: "podéis", ellos: "pueden" }
    )
  );
  renderGroup(
    "Stem-changing: e → i",
    "bg-sand-100 text-sand-700",
    "Only in -ir verbs. The 'e' becomes 'i' in all forms except nosotros and vosotros. Examples: pedir → pido, servir → sirvo.",
    eToI,
    bootDiagram(
      "pedir (to ask for)",
      "servir → sirvo, repetir → repito, seguir → sigo",
      { yo: "pido", tu: "pides", el: "pide", nos: "pedimos", vos: "pedís", ellos: "piden" }
    )
  );
  if (otherStem.length)
    renderGroup(
      "Other stem-changers",
      "bg-sand-100 text-sand-700",
      "Less common patterns (u→ue, i→ie, etc).",
      otherStem,
      bootDiagram(
        "jugar (to play) — u→ue, the only one!",
        "adquirir → adquiero (i→ie)",
        { yo: "juego", tu: "juegas", el: "juega", nos: "jugamos", vos: "jugáis", ellos: "juegan" }
      )
    );
  renderGroup(
    "Yo-only irregulars",
    "bg-red-100 text-red-700",
    "These verbs are completely regular EXCEPT in the 'yo' (I) form of the present tense. Examples: hacer → hago, poner → pongo, salir → salgo.",
    yoOnly,
    yoOnlyDiagram()
  );
  renderGroup(
    "Fully irregular",
    "bg-red-100 text-red-700",
    "These verbs deviate across multiple forms and tenses. The big ones — ser, estar, ir, tener — must simply be memorized.",
    fullyIrregular,
    fullyIrregularDiagram()
  );

  window.scrollTo({ top: 0, behavior: "smooth" });
}
